import { useParams } from 'react-router-dom';
import { BillInvoices } from './Invoices';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ACK_STATUS_LABELS, LEDGER_KIND_LABELS, type LedgerKind } from '@shared/domain';
import { api, actionKey } from '../../api';
import { useSession } from '../../session';
import type { Statement, StatementLine } from '../../types';
import { Badge, ConfirmDialog, ErrorBox, Field, Loading, day, dt, inr, useBusy } from '../../ui';
import { billName, download } from './RelationshipParts';

function Lines({ title, lines }: { title: string; lines: StatementLine[] }) {
  return (
    <div className="card">
      <h2>{title}</h2>
      {!lines.length ? <p className="muted">None.</p> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Ref</th><th>Details</th><th>Driver / vehicle</th><th className="num">Qty</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.ledgerEntryId}>
                  <td>{dt(l.occurredAt)}</td>
                  <td className="mono">{l.reference}</td>
                  <td>{LEDGER_KIND_LABELS[l.kind as LedgerKind]}{l.manual ? ' (by hand)' : ''}<div><small>{l.description}</small></div>
                    {l.ackStatus ? <Badge tone={l.ackStatus === 'PENDING' ? 'warn' : 'ok'}>{ACK_STATUS_LABELS[l.ackStatus]}</Badge> : null} {l.disputed ? <Badge tone="bad">disputed</Badge> : null}
                  </td>
                  <td>{l.personName}{l.vehicleRegistration ? ` · ${l.vehicleRegistration}` : ''}</td>
                  <td className="num">{l.quantity ? `${l.quantity} ${l.unit}` : ''}</td>
                  <td className="num">{inr(l.amountPaise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function StatementView() {
  const { id } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['statement', id], queryFn: () => api.get<Statement>(`/api/statements/${id}`) });
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const { busy, error, run } = useBusy();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const s = q.data!;
  return (
    <div className="stack">
      <div className="card">
        <div className="row between">
          <h1>{billName(s.periodStart, s.periodEnd)} · {s.number}</h1>
          {s.status === 'SUPERSEDED' ? <Badge>replaced by a newer version</Badge> : <Badge tone="ok">issued</Badge>}
        </div>
        <p>{s.supplierName} → {s.buyerName} · {day(s.periodStart)} – {day(s.periodEnd)} · issued {dt(s.issuedAt)} by {s.issuedBy.name}</p>
        {s.correctionNote ? <div className="alert info">Correction: {s.correctionNote}</div> : null}
        <div className="grid three">
          <div className="stat"><span className="label">Previous balance</span><span className="value">{inr(s.openingOutstandingPaise)}</span></div>
          <div className="stat"><span className="label">New charges</span><span className="value">{inr(s.totals.chargesPaise)}</span></div>
          <div className="stat"><span className="label">Payments & reductions</span><span className="value">{inr(s.totals.creditsPaise)}</span></div>
          <div className="stat big"><span className="label">Amount due</span><span className="value">{inr(s.totals.closingOutstandingPaise)}</span></div>
          <div className="stat"><span className="label">Pay by</span><span className="value">{day(s.dueDate)}</span></div>
          {BigInt(s.totals.disputedPaise) > 0n ? <div className="stat bad"><span className="label">Of which disputed</span><span className="value">{inr(s.totals.disputedPaise)}</span></div> : null}
        </div>
        {s.totals.unacknowledgedCount ? <p className="muted">{s.totals.unacknowledgedCount} supply(ies) still waiting for driver confirmation.</p> : null}
        <div className="row">
          <button className="btn" onClick={() => void download(`/api/statements/${s._id}/pdf`, `${s.number}.pdf`)}>Download PDF</button>
          <button className="btn" onClick={() => void download(`/api/statements/${s._id}/csv`, `${s.number}.csv`)}>Download CSV</button>
          {can('supplier.statements.issue') && s.status === 'ISSUED' ? <button className="btn" onClick={() => setOpen(true)}>Issue corrected version</button> : null}
        </div>
      </div>
      <Lines title="Charges" lines={s.charges ?? []} />
      <section className="card"><h2>Invoices from the pump</h2><BillInvoices relationshipId={s.relationshipId} statementId={s._id} /></section>
      <Lines title="Payments and reductions" lines={s.credits ?? []} />
      <ConfirmDialog open={open} title="Issue a corrected version?" lines={[['Statement', s.number], ['Period', `${day(s.periodStart)} – ${day(s.periodEnd)}`]]} confirmLabel="Issue new version" busy={busy} error={error} onClose={() => setOpen(false)} onConfirm={() => void run(async () => { const k = actionKey(`correct:${s._id}`); const n = await api.post<Statement>(`/api/statements/${s._id}/correct`, { note }, { idempotencyKey: k.key }); k.done(); setOpen(false); await qc.invalidateQueries(); window.location.href = `/statements/${n._id}`; })}>
        <p className="muted">The current version is kept unchanged and marked as replaced. Money corrections are made as separate adjustments first.</p>
        <Field label="What changed?"><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </ConfirmDialog>
    </div>
  );
}
